// src/pages/admin/components/MediaPicker.jsx

import React from 'react';
import styled from 'styled-components';
import { AdminAPI } from '../../../api/admin';
import { normalizeMediaUrl } from '../../../api/base';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--bdr);
  border-radius: 8px;
  background: var(--bg);
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  background: #f5f5f5;
`;

const NoImagePlaceholder = styled.div`
  width: 80px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 6px;
  color: #999;
  font-size: 12px;
  text-align: center;
`;

const ImageInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ImageName = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const ImageMeta = styled.div`
  font-size: 12px;
  color: #666;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 8px 12px;
  border: 1px solid var(--bdr);
  background: var(--card);
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--bg);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f5f5f5;
  }
  
  &.primary {
    background: var(--primary);
    color: var(--primaryFg);
    border-color: var(--primary);
  }
  
  &.primary:hover:not(:disabled) {
    background: var(--primary);
    opacity: 0.9;
  }
  
  &.danger {
    color: #dc3545;
  }
  
  &.danger:hover:not(:disabled) {
    background: #ffebee;
    border-color: #f44336;
  }
  
  &.loading {
    position: relative;
    color: transparent;
  }
  
  &.loading::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;
    border: 2px solid #ccc;
    border-top: 2px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  &.selecting {
    background: #fff3cd;
    border-color: #ffc107;
    color: #856404;
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--card);
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--bdr);
  border-radius: 6px;
  background: var(--bg);
  color: var(--fg);
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MediaItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 2px solid ${props => props.selected ? 'var(--primary)' : '#ddd'};
  border-radius: 8px;
  background: var(--card);
  cursor: pointer;
  
  &:hover {
    border-color: var(--primary);
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  background: #f5f5f5;
`;

const MediaName = styled.div`
  font-size: 10px;
  color: #666;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export default function MediaPicker({ value, onChange, label = "Изображение" }) {
  const [showModal, setShowModal] = React.useState(false);
  const [mediaItems, setMediaItems] = React.useState([]);
  const [selectedMedia, setSelectedMedia] = React.useState(null);
  const [currentMedia, setCurrentMedia] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loadingCurrent, setLoadingCurrent] = React.useState(false);
  const [selecting, setSelecting] = React.useState(false); // индикация выбора медиа
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const pageSize = 20;

  // Загружаем информацию о текущем медиа
  React.useEffect(() => {
    if (value) {
      loadCurrentMedia(value);
    } else {
      setCurrentMedia(null);
    }
  }, [value]);

  const loadCurrentMedia = async (mediaId) => {
    setLoadingCurrent(true);
    try {
      const media = await AdminAPI.getMediaFile(mediaId);
      setCurrentMedia(media);
    } catch (error) {
      console.error('Failed to load current media:', error);
      setCurrentMedia(null);
    } finally {
      setLoadingCurrent(false);
    }
  };

  const loadMediaLibrary = async () => {
    setLoading(true);
    try {
      const response = await AdminAPI.listMediaLibrary({
        page,
        page_size: pageSize,
        q: search || undefined,
        mime: 'image/*'
      });
      
      setMediaItems(response.media || []);
      setTotalPages(Math.ceil((response.pagination?.total || 0) / pageSize));
    } catch (error) {
      console.error('Failed to load media library:', error);
      setMediaItems([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (showModal) {
      loadMediaLibrary();
    }
  }, [showModal, page, search]);

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const handleSelectMedia = (media) => {
    setSelecting(true);
    setSelectedMedia(media);
    
    // Небольшая задержка для визуальной обратной связи
    setTimeout(() => {
      setSelecting(false);
    }, 300);
  };

  const handleConfirmSelection = async () => {
    if (selectedMedia) {
      setSelecting(true);
      
      // Небольшая задержка для показа процесса
      await new Promise(resolve => setTimeout(resolve, 200));
      
      onChange(selectedMedia.id);
      setCurrentMedia(selectedMedia);
      setShowModal(false);
      setSelectedMedia(null);
      setSelecting(false);
    }
  };

  const handleRemoveMedia = () => {
    onChange(null);
    setCurrentMedia(null);
  };

  const handleOpenModal = () => {
    setSelecting(true);
    
    // Небольшая задержка для показа процесса открытия
    setTimeout(() => {
      setShowModal(true);
      setSelectedMedia(null);
      setSearch('');
      setPage(1);
      setSelecting(false);
    }, 200);
  };

  return (
    <Container>
      <label style={{ fontWeight: 600, marginBottom: '6px', display: 'block' }}>
        {label}
      </label>
      
      <PreviewContainer>
        {loadingCurrent ? (
          <>
            <NoImagePlaceholder>
              Загрузка...
            </NoImagePlaceholder>
            <ImageInfo>
              <ImageName>Загрузка изображения...</ImageName>
              <ImageMeta>Пожалуйста, подождите</ImageMeta>
            </ImageInfo>
            <ButtonGroup>
              <Button disabled>Загрузка...</Button>
            </ButtonGroup>
          </>
        ) : currentMedia ? (
          <>
            <PreviewImage 
              src={normalizeMediaUrl(currentMedia.thumbnail_url || currentMedia.url)} 
              alt={currentMedia.original_filename || currentMedia.filename}
            />
            <ImageInfo>
              <ImageName>{currentMedia.original_filename || currentMedia.filename}</ImageName>
              <ImageMeta>ID: {currentMedia.id}</ImageMeta>
            </ImageInfo>
            <ButtonGroup>
              <Button 
                className={`primary ${selecting ? 'selecting' : ''}`} 
                onClick={handleOpenModal}
                disabled={selecting}
              >
                {selecting ? 'Открываем...' : 'Изменить'}
              </Button>
              <Button className="danger" onClick={handleRemoveMedia}>
                Удалить
              </Button>
            </ButtonGroup>
          </>
        ) : (
          <>
            <NoImagePlaceholder>
              Нет изображения
            </NoImagePlaceholder>
            <ImageInfo>
              <ImageName>Изображение не выбрано</ImageName>
              <ImageMeta>Выберите изображение из медиа-библиотеки</ImageMeta>
            </ImageInfo>
            <ButtonGroup>
              <Button 
                className={`primary ${selecting ? 'selecting' : ''}`} 
                onClick={handleOpenModal}
                disabled={selecting}
              >
                {selecting ? 'Открываем...' : 'Выбрать'}
              </Button>
            </ButtonGroup>
          </>
        )}
      </PreviewContainer>

      {showModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <ModalContent>
            <h3>Выбор изображения</h3>
            
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Поиск по названию файла..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span style={{ fontSize: '14px', color: '#666' }}>
                Всего: {mediaItems.length}
              </span>
            </SearchContainer>

            <Pagination>
              <div>
                <Button 
                  disabled={page <= 1} 
                  onClick={() => setPage(p => p - 1)}
                >
                  ← Назад
                </Button>
                <span style={{ margin: '0 12px', fontSize: '14px' }}>
                  Страница {page} из {totalPages}
                </span>
                <Button 
                  disabled={page >= totalPages} 
                  onClick={() => setPage(p => p + 1)}
                >
                  Вперед →
                </Button>
              </div>
            </Pagination>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ 
                  display: 'inline-block',
                  width: '32px',
                  height: '32px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid var(--primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '12px'
                }} />
                <div>Загрузка медиа-библиотеки...</div>
              </div>
            ) : mediaItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {search ? 'Ничего не найдено по запросу' : 'Медиа-библиотека пуста'}
              </div>
            ) : (
              <MediaGrid>
                {mediaItems.map((media) => (
                  <MediaItem
                    key={media.id}
                    selected={selectedMedia?.id === media.id}
                    onClick={() => handleSelectMedia(media)}
                  >
                    <MediaImage
                      src={normalizeMediaUrl(media.thumbnail_url || media.url)}
                      alt={media.original_filename || media.filename}
                    />
                    <MediaName>
                      {media.original_filename || media.filename}
                    </MediaName>
                  </MediaItem>
                ))}
              </MediaGrid>
            )}

            <ModalActions>
              <Button onClick={() => setShowModal(false)}>
                Отмена
              </Button>
              <Button 
                className={`primary ${selecting ? 'selecting' : ''}`} 
                onClick={handleConfirmSelection}
                disabled={!selectedMedia || selecting}
              >
                {selecting ? 'Применяем...' : 'Выбрать'}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}